import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Server,
  Boxes,
  Coffee,
  ShieldCheck,
  Network,
  Cloud,
  Workflow,
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import WebCenterStackDiagram from '@/components/diagrams/WebCenterStackDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    label: 'December 2026',
    title: 'WebLogic 12c (12.2.1.4) Premier Support ends',
    detail:
      'Oracle WebLogic Server 12.2.1.4 — the runtime under WebCenter Content, Imaging, Capture, Forms Recognition, SOA Suite, BPM, Portal, and most Fusion Middleware applications — exits Premier Support in December 2026. The cliff is the same as the WebCenter 12c cliff because WebCenter sits on WebLogic.',
    severity: 'amber',
  },
  {
    label: 'WebLogic 14c (14.1.2 LTS)',
    title: 'The forward path',
    detail:
      'WebLogic 14.1.2 is the Long Term Support release. Premier Support runs through December 2030; Extended Support through December 2033. Java 17 and Java 21 support, TLS 1.3, Kubernetes-friendly deployment, OCI integration.',
    severity: 'green',
  },
  {
    label: 'Domain reconfiguration',
    title: 'Irreversible — backup is critical',
    detail:
      'The 12c → 14c upgrade runs reconfig.sh against the existing 12c domain. Once reconfigured into 14c, the domain cannot be reverted to 12c. A clean offline backup of the 12c domain home, RCU schemas, and managed-server configuration is the only rollback path.',
    severity: 'red',
  },
];

const WHATS_NEW = [
  {
    icon: Coffee,
    title: 'Java 17 and Java 21 support',
    detail:
      'WebLogic 14c (14.1.2) certifies on modern JDK versions. 12c (12.2.1.4) ran on Java 8 and Java 11. The customer-side Java upgrade is part of the WebLogic upgrade scope — and the largest source of application compatibility work.',
  },
  {
    icon: ShieldCheck,
    title: 'TLS 1.3 and modern JDK security defaults',
    detail:
      'Native TLS 1.3 across the listen-port surface, stronger default cipher suites, and the JDK 17+ security baseline. Legacy 12c integrations pinned to TLS 1.0 / 1.1 or weak cipher suites are surfaced and replaced during the upgrade.',
  },
  {
    icon: Boxes,
    title: 'Kubernetes-friendly deployment',
    detail:
      'WebLogic Kubernetes Operator support, container-aware lifecycle, and patterns for running WebLogic managed servers as pods on OCI Kubernetes Engine. The same domain model still applies — admin server, managed servers, clusters — now containerized.',
  },
  {
    icon: Cloud,
    title: 'OCI integration',
    detail:
      'WebLogic 14c lines up with the OCI deployment patterns used by WebCenter 14c, Fusion Middleware 14c, and Oracle\'s reference architectures — Object Storage, Vault, Resource Manager, and OCI Database Service for the RCU schema tier.',
  },
];

const MIGRATION_PATTERN = [
  {
    step: '1',
    title: 'Offline backup of the 12c environment',
    body: 'Full offline backup of the 12c Oracle home, domain home, application binaries, managed-server configuration, RCU schemas, and any custom deployments. Domain reconfiguration is irreversible — this backup is the only rollback path.',
  },
  {
    step: '2',
    title: 'Install the 14c Oracle home alongside 12c',
    body: 'WebLogic 14.1.2 binaries install into a new Oracle home on the same host (or on a parallel host for cluster topologies). The 12c Oracle home is left in place so the domain still runs while the upgrade is staged.',
  },
  {
    step: '3',
    title: 'Upgrade RCU schemas with the Upgrade Assistant',
    body: 'Oracle\'s Upgrade Assistant (ua) is run from the new 14c Oracle home against the existing RCU schemas in the database — Metadata Services (MDS), Service Table (STB), OPSS, IAU, and component-specific schemas (WLS, SOAINFRA, OCS, IPM, CAPTURE, etc.). Schemas are upgraded in place.',
  },
  {
    step: '4',
    title: 'Reconfigure the domain with reconfig.sh',
    body: 'reconfig.sh from the 14c Oracle home is run against the existing 12c domain directory. The domain is reconfigured to the 14c version, JDBC and JMS resources are re-mapped, and the admin server and managed servers are pointed at the 14c Oracle home. This step is irreversible.',
  },
  {
    step: '5',
    title: 'Validate managed servers and redeploy applications',
    body: 'Admin server is started against the 14c Oracle home, then each managed server is started and inspected — JNDI tree, JDBC pools, JMS destinations, work managers, security realm. Application archives (EAR, WAR) are redeployed against the new domain and validated.',
  },
  {
    step: '6',
    title: 'Integration testing across the stack',
    body: 'Functional regression across every workflow that touches WebLogic — WebCenter Content check-in / retrieval, SOA composite execution, BPM task flows, Forms Recognition extraction, ADF page navigation, EBS / Fusion / IDCS integration, load-balancer health checks, cluster failover.',
  },
];

const SCENARIOS = [
  {
    icon: Network,
    title: 'Mixed 11g and 12c WebLogic across environments',
    body: 'Production on 12.2.1.4, lower environments still on 10.3.6 (11g) or 12.1.3 — common where lower environments were spun up for an old project and never refreshed. The 14c upgrade plan needs to reconcile the topology before the production cutover.',
  },
  {
    icon: Workflow,
    title: 'Custom Java callouts that need recompiling',
    body: 'SOA composite Java embedding, custom WCC services compiled against the 12c API surface, ADF managed beans, custom work managers — anything compiled against 12c + Java 8 / 11 needs to be recompiled against 14c + Java 17 / 21 and validated. This is usually the largest single piece of scope.',
  },
  {
    icon: Server,
    title: 'Clustering and load-balancer integration',
    body: 'Multi-node clusters with a fronting load balancer (OCI Load Balancer, F5, or NGINX) need a coordinated upgrade window — managed servers go offline during reconfiguration, health checks need to be paused, and session replication behavior changes between releases.',
  },
  {
    icon: ShieldCheck,
    title: 'WebLogic + RAC database tier upgrade coordination',
    body: 'Many WebLogic 12c estates sit on Oracle Database 12c or 19c with RAC. The 14c upgrade window is often the right moment to also lift the database tier — but the two upgrades have separate change windows, separate RCU prereqs, and a specific sequencing (database first, RCU schemas next, domain reconfiguration last).',
  },
];

const ENGAGEMENT_TIERS = [
  {
    eyebrow: 'Tier 1 · Upgrade Assessment',
    title: 'Fixed-fee inventory and forward plan',
    body: 'Andrew reads your existing WebLogic 12c topology directly — domain configuration, managed servers, clusters, JDBC pools, JMS destinations, security realms, custom deployments, RCU schema state. Output is a 12c → 14c upgrade plan with Java version target, deprecated-feature watchlist, and a sequenced runbook. Plan is yours to keep regardless of which delivery partner you choose.',
  },
  {
    eyebrow: 'Tier 2 · Architecture Review',
    title: 'Deeper read of WebLogic and what it runs',
    body: 'A one-week fixed-fee read of the running WebLogic estate — domain archeology, cluster topology, integration points, custom Java callouts compiled against 12c, ADF customizations, SOA composites, EBS / Fusion / IDCS integration. Structured inventory used as input to any 14c upgrade or modernization decision.',
  },
  {
    eyebrow: 'Tier 3 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through your upgrade window',
    body: 'Monthly retainer with Andrew, sized for the customer working through a multi-month upgrade decision with internal stakeholders, procurement, and Oracle. Strategic guidance on Java target version, container-vs-bare-metal deployment, RAC tier coordination, and 14c-specific architectural decisions.',
  },
  {
    eyebrow: 'Tier 4 · Forward Deployed Engineering',
    title: 'Hands-on 12c → 14c delivery',
    body: 'Project-based, founder-led delivery of the 14c upgrade itself — RCU schema upgrade, domain reconfiguration, custom Java callout recompilation, ADF 14c compatibility work, cluster cutover, integration testing. For customers who want the WebLogic upgrade run by the same person who can read the existing 12c domain directly.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle WebLogic 12c being killed?',
    a: 'Premier Support for Oracle WebLogic Server 12.2.1.4 ends December 2026 per Oracle\'s Lifetime Support Policy. The product still runs after that date — but new feature work, OS certifications, and non-critical fixes stop. Extended Support and (eventually) Sustaining Support remain available at additional cost. The cliff is the same December 2026 date that hits WebCenter 12c because WebCenter is deployed on WebLogic.',
  },
  {
    q: 'What\'s the WebLogic 12c → 14c upgrade pattern?',
    a: 'The 12c → 14c upgrade installs the WebLogic 14.1.2 Oracle home alongside the existing 12c home, takes a full offline backup, runs Oracle\'s Upgrade Assistant against the RCU schemas, then runs reconfig.sh from the 14c Oracle home against the existing 12c domain directory. Restart the admin server and managed servers against the 14c Oracle home and validate. Applications are then redeployed and regression-tested.',
  },
  {
    q: 'Is the domain reconfiguration reversible?',
    a: 'No — once reconfig.sh has reconfigured a 12c domain to 14c, the domain cannot be reverted in place. Rollback requires restoring the offline backup of the 12c domain home, the 12c Oracle home, and the pre-upgrade RCU schemas from database backup. A clean, tested offline backup of all three is the only rollback path — this is the single most important pre-upgrade activity.',
  },
  {
    q: 'What\'s the Java version impact?',
    a: 'WebLogic 14.1.2 certifies on Java 17 and Java 21, while WebLogic 12.2.1.4 ran on Java 8 and Java 11 — so the customer-side Java upgrade is part of the WebLogic upgrade scope. Custom ADF applications, SOA composites with custom Java callouts, and any compiled-against-12c custom WCC services need to be reviewed for Java 17 / 21 compatibility. Third-party libraries pinned to older JDK versions are a common surprise.',
  },
  {
    q: 'What about WebLogic 11g (10.3.6) environments?',
    a: 'WebLogic 11g cannot go directly to 14c — the supported path is 11g → 12.2.1.4 → 14.1.2, run as two separate domain reconfigurations. WebLogic 11g has been out of Premier Support since 2018 and out of Extended Support since 2021. Most 14c upgrade engagements that touch 11g lower environments end up rebuilding those environments fresh on 14c rather than doing the double-hop.',
  },
  {
    q: 'How do clustered domains affect the upgrade?',
    a: 'Clustered domains are upgraded as a unit — the admin server and all managed servers in the domain are reconfigured together, and the cluster goes offline for the reconfiguration window. Multi-node clusters with a fronting load balancer (OCI Load Balancer, F5, NGINX) need a coordinated change window with load-balancer health checks paused. Session replication and work manager behavior change between releases and need explicit validation.',
  },
  {
    q: 'How does the database (RAC) tier coordinate with the WebLogic upgrade?',
    a: 'The database tier must be at a supported version for the 14.1.2 RCU schema upgrade, and the sequencing is database tier first (if it\'s also being lifted), then RCU schema upgrade with the 14c Upgrade Assistant, then domain reconfiguration. Many 12c WebLogic estates sit on Oracle Database 12c or 19c, often with RAC, and the RCU schemas live in that database. The database and WebLogic upgrades have separate change windows.',
  },
  {
    q: 'Sustaining Support vs Market Driven Support — what\'s the cost trade-off after December 2027?',
    a: 'After Extended Support ends, the options are Sustaining Support (included in maintenance, but limited — no new patches, no security updates, no certifications) or Market Driven Support (a yearly-renewable program that continues critical patching for an additional fee on top of standard maintenance). Market Driven Support typically costs more per year than the maintenance line itself. For most customers, the 14c upgrade is cheaper across a 4-year window than running 12c on Market Driven Support — but the calculus depends on the size of the estate and the planned application life.',
  },
];

const OracleWebLogic14cUpgrade = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const title =
    'Oracle WebLogic Server 12c to 14c Upgrade — The December 2026 Runtime Decision | EZ Cloud';
  const description =
    'Oracle WebLogic Server 12c (12.2.1.4) Premier Support ends December 2026. WebLogic is the runtime under WebCenter, SOA Suite, BPM, and most Fusion Middleware applications. The recommended forward path is the upgrade to WebLogic 14c (14.1.2 LTS) which carries support through 2030 (Premier) / 2033 (Extended). EZ Cloud delivers WebLogic 14c upgrade planning, domain reconfiguration, and integration testing.';
  const canonical = 'https://ezcloud.ai/solutions/oracle-weblogic-14c-upgrade';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: title,
    description,
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical,
    },
    about: [
      'Oracle WebLogic Server',
      'Oracle WebLogic 12c',
      'Oracle WebLogic 14c',
      'Oracle Fusion Middleware',
      'Java EE',
      'JEE Application Server',
    ].map((name) => ({ '@type': 'Thing', name })),
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to upgrade Oracle WebLogic Server from 12c to 14c',
    description: 'The Oracle WebLogic Server 12c to 14c (14.1.2 LTS) upgrade pattern. Irreversible domain reconfiguration via reconfig.sh, schema upgrade, and managed server validation.',
    totalTime: 'P3M',
    step: MIGRATION_PATTERN.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
      url: 'https://ezcloud.ai/solutions/oracle-weblogic-14c-upgrade#step-' + (idx + 1),
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Oracle WebLogic 14c Upgrade',
            url: canonical,
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Server className="w-4 h-4" aria-hidden />
              Oracle WebLogic Server · 12c → 14c · The runtime decision
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebLogic Server 12c → 14c upgrade
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                The runtime under WebCenter, SOA, and BPM. Same December 2026 cliff.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              WebLogic is the runtime under WebCenter, SOA Suite, and BPM. Same December 2026 cliff.
              The forward path is WebLogic 14c (14.1.2 LTS) — Premier Support through 2030, Extended
              through 2033.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('weblogic-14c-upgrade-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebLogic Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR — definitional summary for AEO synthesis */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle WebLogic Server 12c → 14c upgrade, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebLogic Server is the JEE application server that runs Oracle Fusion
                  Middleware
                </strong>{' '}
                — WebCenter Content (UCM), WebCenter Imaging, WebCenter Enterprise Capture,
                WebCenter Forms Recognition, SOA Suite, BPM, ADF applications, and most other Oracle
                Fusion Middleware products. If you run WebCenter, you run WebLogic.
              </p>
              <p>
                Oracle WebLogic Server 12c (release 12.2.1.4) Premier Support ends{' '}
                <strong className="text-gray-900">December 2026</strong> — the same cliff that hits
                WebCenter 12c, because WebCenter is deployed on WebLogic. The recommended forward
                path is the upgrade to{' '}
                <strong className="text-gray-900">WebLogic 14c (release 14.1.2 LTS)</strong>, which
                carries Premier Support through December 2030 and Extended Support through December
                2033.
              </p>
              <p>
                The upgrade pattern installs the 14c Oracle home alongside the existing 12c Oracle
                home, runs the Upgrade Assistant against the RCU schemas, and runs reconfig.sh from
                the 14c Oracle home against the existing 12c domain.{' '}
                <strong className="text-gray-900">
                  Once reconfigured, the 12c domain cannot be reverted in place
                </strong>{' '}
                — a clean offline backup of the domain, Oracle home, and RCU schemas is the only
                rollback path.
              </p>
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The frame
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Three facts that shape the WebLogic 14c decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The December 2026 cliff, the 14c LTS support window, and the irreversibility of domain
              reconfiguration together define how every WebLogic 12c customer should plan the
              upgrade window.
            </p>
            <div className="space-y-5">
              {KEY_FACTS.map((d) => (
                <Card
                  key={d.label}
                  className={`p-7 bg-white border shadow-sm ${
                    d.severity === 'red'
                      ? 'border-red-200'
                      : d.severity === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.severity === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.label}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What WebLogic is in the WebCenter AP context */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What WebLogic is
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              WebLogic Server in the WebCenter AP context
            </h2>
            <div className="max-w-5xl mx-auto mb-12">
              <WebCenterStackDiagram />
            </div>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              WebLogic is the JEE application server every WebCenter and SOA component is deployed
              into. The 14c upgrade is a runtime upgrade — the applications above it move with it.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Server className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">JEE application server</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      WebLogic Server is Oracle’s JEE-compliant application server. Every
                      Fusion Middleware product — WebCenter Content, Imaging, Capture, Forms
                      Recognition, SOA Suite, BPM, Portal — is packaged as a JEE application
                      deployed onto a WebLogic domain.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Boxes className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">
                      Domain, admin server, managed servers, clusters
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      A WebLogic deployment is a domain. The domain has one admin server and one or
                      more managed servers. Managed servers can be grouped into clusters for high
                      availability and load balancing. Each Fusion Middleware product runs on its
                      own managed server (or cluster) inside the domain.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Network className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">
                      Node managers, JNDI tree, JDBC pool management
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Node managers start and stop managed servers across hosts. The JNDI tree
                      surfaces datasources, JMS destinations, EJBs, and resource bindings to
                      deployed applications. JDBC pools manage database connections — typically to
                      the RCU schemas and to EBS / Fusion / custom datasources.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Workflow className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">What it runs in an AP stack</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      WebCenter Content (UCM), Imaging, Capture, Forms Recognition, SOA Suite, BPM,
                      and ADF applications all deploy onto WebLogic managed servers. The WebLogic
                      upgrade moves the runtime — the AP applications come along with the domain
                      reconfiguration.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* The forward path — 12c → 14c (14.1.2) */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The forward path
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              WebLogic 12c → 14c (14.1.2) — the irreversible reconfiguration pattern
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The Oracle-recommended upgrade pattern stages the 14c Oracle home alongside 12c, then
              uses reconfig.sh to move the existing domain forward.
            </p>
            <Card className="p-7 bg-white border border-gray-200 shadow-sm mb-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <RotateCcw className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Once reconfigured, the 12c domain cannot be reverted
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    reconfig.sh upgrades the existing domain directory in place — the listen ports,
                    JDBC datasources, JMS destinations, security realm, and managed-server
                    configuration are migrated forward into the 14c version. There is no
                    documented in-place downgrade. The only rollback path is to restore the offline
                    backup of the domain home, the 12c Oracle home, and the pre-upgrade RCU schemas
                    from database backup. This is why the offline backup in step 1 is the single
                    most important pre-upgrade activity.
                  </p>
                </div>
              </div>
            </Card>
            <div className="space-y-5">
              {MIGRATION_PATTERN.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's new in WebLogic 14c */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What's new in WebLogic 14c
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four meaningful shifts in WebLogic 14.1.2
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              14c lifts the JDK version, modernizes the TLS and security defaults, opens a clean
              path to container deployment, and lines up with the OCI deployment patterns used by
              Fusion Middleware 14c.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHATS_NEW.map(({ icon: Icon, title: t, detail }) => (
                <Card key={t} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{t}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Java version planning */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-amber-50 border border-amber-200 shadow-sm">
              <div className="flex items-start gap-4">
                <AlertTriangle
                  className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1"
                  aria-hidden
                />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">
                    Java version planning
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    14c removes support for legacy Java versions
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    WebLogic 14.1.2 certifies on Java 17 and Java 21. The Java 8 and Java 11 runtimes
                    used by WebLogic 12c are not supported. The customer-side Java upgrade is part
                    of the WebLogic upgrade scope and needs an explicit application-compatibility
                    pass:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Custom ADF applications — managed beans, task flow code, custom converters
                      compiled against ADF + Java 8 / 11
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      SOA composites with custom Java callouts — embedded Java activities, custom
                      XPath functions, custom adapters
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Custom WCC services and components — compiled against the 12c API surface and
                      the older JDK
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Third-party libraries pinned to a specific JDK — the most common source of
                      late-stage surprises
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Scenarios — what we hear */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Common WebLogic scenarios in the 12c → 14c window
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the four patterns that come up most often in conversations with customers
              planning the WebLogic 14c upgrade.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title: t, body }) => (
                <Card key={t} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-2">{t}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Related: WebCenter, EOS decision, 14c upgrade, SOA Suite */}
        <section className="py-12 bg-gray-50 border-y border-gray-200">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Related decisions
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-6">
              WebLogic sits inside a bigger Fusion Middleware decision
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
              The WebLogic 14c upgrade is one piece of the December 2026 Fusion Middleware decision.
              These are the adjacent decisions most customers are running in parallel.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="block p-5 bg-white border border-gray-200 rounded-lg hover:border-accent hover:shadow-sm transition"
              >
                <p className="font-semibold text-gray-900 mb-1">Modernize Oracle WebCenter</p>
                <p className="text-sm text-gray-600">
                  The hub page covering the four forward paths from the December 2026 cliff.
                </p>
              </Link>
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="block p-5 bg-white border border-gray-200 rounded-lg hover:border-accent hover:shadow-sm transition"
              >
                <p className="font-semibold text-gray-900 mb-1">
                  WebCenter 12c end-of-support decision
                </p>
                <p className="text-sm text-gray-600">
                  Premier ends December 2026, Extended December 2027 — the application-tier
                  decision that sits on top of WebLogic.
                </p>
              </Link>
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="block p-5 bg-white border border-gray-200 rounded-lg hover:border-accent hover:shadow-sm transition"
              >
                <p className="font-semibold text-gray-900 mb-1">WebCenter 12c → 14c upgrade</p>
                <p className="text-sm text-gray-600">
                  The WebCenter-application-tier upgrade that pairs with the WebLogic 14c upgrade.
                </p>
              </Link>
              <Link
                to="/solutions/oracle-soa-suite-migration"
                className="block p-5 bg-white border border-gray-200 rounded-lg hover:border-accent hover:shadow-sm transition"
              >
                <p className="font-semibold text-gray-900 mb-1">Oracle SOA Suite migration</p>
                <p className="text-sm text-gray-600">
                  SOA Suite runs on WebLogic — the SOA-tier decision moves together with the
                  WebLogic upgrade.
                </p>
              </Link>
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on WebLogic 12c → 14c upgrades
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for the WebLogic 14c upgrade
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level Oracle expertise that powers EZ Cloud’s WebCenter
              assessments applies equally to WebLogic 12c → 14c upgrade engagements. Standard
              Consulting is the default; Forward Deployed Engineering is available for customers who
              want the upgrade run hands-on by the same person reading the domain.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.eyebrow} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.eyebrow}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths from the December 2026 cliff —
              including the WebLogic runtime decision — with stack-specific considerations, eight
              diagnostic questions, and a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the WebLogic 12c → 14c upgrade
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your WebLogic 12c → 14c upgrade
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist with 20+ years specifically on Oracle WebCenter and the WebLogic
              runtime underneath it. Walk through your domain topology, Java target version, and
              the upgrade considerations in the context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('weblogic-14c-upgrade-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WebLogic Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleWebLogic14cUpgrade;
